import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { BASE_URL } from '@/lib/api'

interface AuthStatus {
  mode: 'local' | 'cloud'
  hasGoogle: boolean
}

interface AuthMe {
  authenticated: boolean
  user?: { id: string; workspaceId: string; role: string; email?: string }
}

/** Check auth mode (local vs cloud) — determines login UI. */
export function useAuthStatus() {
  return useQuery({
    queryKey: ['auth-status'],
    queryFn: async (): Promise<AuthStatus> => {
      const res = await fetch(`${BASE_URL}/auth/status`, { credentials: 'include' })
      const body = await res.json()
      return body.data
    },
  })
}

/** Check if currently authenticated. */
export function useAuthMe() {
  return useQuery({
    queryKey: ['auth-me'],
    queryFn: async (): Promise<AuthMe> => {
      const res = await fetch(`${BASE_URL}/auth/me`, { credentials: 'include' })
      const body = await res.json()
      return body.data
    },
    retry: false,
  })
}

/** Password login (local mode only). */
export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (password: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error(body.error?.message ?? '로그인 실패')
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth-me'] }),
  })
}

/** Logout. */
export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await fetch(`${BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth-me'] })
      qc.clear()
    },
  })
}

/** Google OAuth login URL (cloud mode). */
export function getGoogleLoginUrl(): string {
  return `${BASE_URL}/auth/google`
}
